import { annotationHeightM, ANNOT_LAYER } from './annotations'
import { walkPath } from './arcFit'
import {
  dimensionGeometry,
  dimensionHeightM,
  dimMmToDrawing,
  DIM_LAYER,
  DIM_TICK_MM,
} from './dimensions'
import { fadeHex, normalizeHex, SVG_PROJ_FADE } from './drawingToSvg'
import type { Drawing2D, Point2, Polyline2D } from './viewTypes'

// ---------------------------------------------------------------------------
// Writer DXF ASCII R12 (AC1009).
//
// Porquê DXF e não DWG: o DWG é formato proprietário da Autodesk, sem
// especificação pública e sem biblioteca aberta fiável em JS. O DXF é a
// praxis de intercâmbio: qualquer CAD (AutoCAD, BricsCAD, ZWCAD,
// LibreCAD, QCAD, Revit por importação) abre um DXF e grava-o como DWG
// se o utilizador quiser. R12 é a variante mais compatível que existe:
// sem tabelas de handles obrigatórias, sem secção OBJECTS, sem classes.
//
// Unidades: MODEL SPACE 1:1 em METROS. O R12 não tem a variável
// $INSUNITS (só apareceu no R2000/AC1015), por isso a unidade vai num
// COMENTÁRIO (grupo 999) dentro do HEADER, que os CAD ignoram ao ler mas
// que fica no ficheiro para quem o abrir. Quem importar em milímetros
// escala por 1000 no CAD.
//
// Texto: um R12 lê o TEXT pela code page do desenho ($DWGCODEPAGE). O
// cabeçalho declara ANSI_1252 e o ficheiro tem de ser GRAVADO nessa
// code page (`dxfToBytes`): gravado em UTF-8, "Área" lia-se "Ãrea" e
// "m²" lia-se "mÂ²" no CAD.
//
// Camadas: uma por camada do desenho e por tipo de traço,
// `IFCWALL_CUT` e `IFCWALL_PROJ`, para o desenhador poder ligar/desligar
// e atribuir espessuras no CAD (o R12 não tem lineweight por entidade).
//
// Entidades: POLYLINE/VERTEX/SEQEND para polilinhas (R12 não tem
// LWPOLYLINE) e LINE para segmentos soltos de dois pontos.
//
// SEM PREENCHIMENTOS (poché). Decisão assumida: o R12 não tem a entidade
// HATCH (só aparece no R14/AC1014, e mesmo aí com dados de fronteira e
// secção OBJECTS que o R12 não suporta). No R12 uma trama é um BLOCO
// ANÓNIMO com centenas de LINE (ou SOLID triangulados) por cada anel,
// gerado pelo CAD: escrevê-lo à mão obrigava a triangular polígonos com
// furos e multiplicava o tamanho do ficheiro por dezenas, para dar um
// resultado que qualquer CAD faz melhor em dois cliques (HATCH sobre os
// contornos fechados que aqui já vão como POLYLINE com a bandeira 70=1).
// O SVG e o PDF preenchem; o DXF fica com os contornos e o CAD trata do
// resto.
// ---------------------------------------------------------------------------

export interface DxfOptions {
  /** Casas decimais das coordenadas (metros). */
  decimals?: number
  /** Sufixos das camadas. */
  cutSuffix?: string
  projSuffix?: string
  /** Quanto a cor da camada de projecção se aproxima do branco (ver
   *  `lineWeights.ts`). O R12 não tem espessura de linha por entidade,
   *  por isso no DXF a escolha do utilizador chega pela COR: com fade 0
   *  a camada `_PROJ` fica com a mesma cor da `_CUT` e o desenhador vê
   *  no CAD a mesma hierarquia que viu no ecrã. */
  projFade?: number
}

/** Code page declarada no cabeçalho. Tem de ser a mesma com que o
 *  ficheiro é gravado (ver `dxfToBytes`). */
export const DXF_CODEPAGE = 'ANSI_1252'

/** Comentário com a unidade do desenho: o R12 não tem $INSUNITS. */
export const DXF_UNITS_COMMENT =
  'Unidades: metros (model space 1:1). Exportado pelo VN2R openBIM Hub.'

export function drawingToDxf(d: Drawing2D, opts?: DxfOptions): string {
  const decimals = opts?.decimals ?? 6
  const cutSuffix = opts?.cutSuffix ?? '_CUT'
  const projSuffix = opts?.projSuffix ?? '_PROJ'
  const out: string[] = []
  // Os códigos de grupo 60..79 são inteiros de 16 bits (bandeiras,
  // cores, contagens): escrevê-los com casas decimais faz alguns CAD
  // recusarem o ficheiro. Os 10..59 são reais.
  const g = (code: number, value: string | number): void => {
    out.push(String(code))
    if (typeof value !== 'number') {
      out.push(value)
    } else if (code >= 60 && code <= 79) {
      out.push(String(Math.round(value)))
    } else {
      out.push(num(value, decimals))
    }
  }

  // --- HEADER ---------------------------------------------------------------
  g(0, 'SECTION')
  g(2, 'HEADER')
  g(9, '$ACADVER')
  g(1, 'AC1009')
  g(999, DXF_UNITS_COMMENT)
  g(9, '$DWGCODEPAGE')
  g(3, DXF_CODEPAGE)
  g(9, '$EXTMIN')
  g(10, d.bbox.minX)
  g(20, d.bbox.minY)
  g(30, 0)
  g(9, '$EXTMAX')
  g(10, d.bbox.maxX)
  g(20, d.bbox.maxY)
  g(30, 0)
  g(0, 'ENDSEC')

  // --- TABLES ---------------------------------------------------------------
  const layers: Array<{ name: string; aci: number }> = []
  const seen = new Set<string>()
  const addLayer = (name: string, aci: number): string => {
    const safe = dxfLayerName(name)
    if (!seen.has(safe)) {
      seen.add(safe)
      layers.push({ name: safe, aci })
    }
    return safe
  }
  const layerPlan = d.layers.map((l) => {
    const base = normalizeHex(l.colorHex)
    const cutAci = base ? hexToAci(base) : 7
    const projFade =
      typeof opts?.projFade === 'number' && opts.projFade >= 0
        ? opts.projFade
        : SVG_PROJ_FADE
    const projAci = base
      ? hexToAci(projFade > 0 ? fadeHex(base, projFade) : base)
      : 8
    return {
      layer: l,
      cutName: addLayer(l.name + cutSuffix, cutAci),
      projName: addLayer(l.name + projSuffix, projAci),
    }
  })
  const notes = d.annotations ?? []
  // 7 = cor que segue o fundo do ecrã no CAD: é o que um texto de
  // anotação quer, tal como o traço a preto e branco.
  const annotLayer = notes.length > 0 ? addLayer(ANNOT_LAYER, 7) : null
  const dims = d.dimensions ?? []
  const dimLayer = dims.length > 0 ? addLayer(DIM_LAYER, 7) : null
  // Tracejado só é preciso quando há cotas de área. Declara-se sempre
  // que haja cotas: uma LTYPE a mais na tabela não custa nada e evita
  // uma entidade a referir um tipo de linha inexistente.
  const withDashed = dims.length > 0

  g(0, 'SECTION')
  g(2, 'TABLES')
  g(0, 'TABLE')
  g(2, 'LTYPE')
  g(70, withDashed ? 2 : 1)
  g(0, 'LTYPE')
  g(2, 'CONTINUOUS')
  g(70, 0)
  g(3, 'Solid line')
  g(72, 65)
  g(73, 0)
  g(40, 0)
  if (withDashed) {
    // Padrão em UNIDADES DO DESENHO (metros): traço de 0,5 m e
    // intervalo de 0,25 m. À escala de uma planta lê-se como tracejado
    // sem o CAD ter de mexer no $LTSCALE. 73 = número de elementos do
    // padrão, 40 = comprimento total, 49 = cada elemento (positivo =
    // traço, negativo = espaço).
    g(0, 'LTYPE')
    g(2, 'DASHED')
    g(70, 0)
    g(3, '__ __ __')
    g(72, 65)
    g(73, 2)
    g(40, 0.75)
    g(49, 0.5)
    g(49, -0.25)
  }
  g(0, 'ENDTAB')
  g(0, 'TABLE')
  g(2, 'LAYER')
  g(70, layers.length + 1)
  // Camada 0 obrigatória.
  g(0, 'LAYER')
  g(2, '0')
  g(70, 0)
  g(62, 7)
  g(6, 'CONTINUOUS')
  for (const l of layers) {
    g(0, 'LAYER')
    g(2, l.name)
    g(70, 0)
    g(62, l.aci)
    g(6, 'CONTINUOUS')
  }
  g(0, 'ENDTAB')
  g(0, 'ENDSEC')

  // --- ENTITIES -------------------------------------------------------------
  g(0, 'SECTION')
  g(2, 'ENTITIES')
  /** CIRCLE (R12 nativo). */
  const emitCircle = (
    c: { cx: number; cy: number; r: number },
    layerNameStr: string,
  ): void => {
    g(0, 'CIRCLE')
    g(8, layerNameStr)
    g(10, c.cx)
    g(20, c.cy)
    g(30, 0)
    g(40, c.r)
  }
  /** ARC (R12 nativo). O DXF percorre SEMPRE do ângulo 50 para o 51 no
   *  sentido directo, portanto um arco horário escreve-se trocando os
   *  extremos. */
  const emitArc = (
    arc: { cx: number; cy: number; r: number; a0: number; a1: number; ccw: boolean },
    layerNameStr: string,
  ): void => {
    const deg = (a: number): number => {
      const v = ((a * 180) / Math.PI) % 360
      return v < 0 ? v + 360 : v
    }
    g(0, 'ARC')
    g(8, layerNameStr)
    g(10, arc.cx)
    g(20, arc.cy)
    g(30, 0)
    g(40, arc.r)
    g(50, deg(arc.ccw ? arc.a0 : arc.a1))
    g(51, deg(arc.ccw ? arc.a1 : arc.a0))
  }
  /** Troço só de rectas: LINE quando são dois pontos abertos, senão
   *  POLYLINE/VERTEX/SEQEND. */
  const emitStraight = (
    pts: Point2[],
    closed: boolean,
    layerNameStr: string,
  ): void => {
    if (pts.length < 2) return
    if (pts.length === 2 && !closed) {
      g(0, 'LINE')
      g(8, layerNameStr)
      g(10, pts[0][0])
      g(20, pts[0][1])
      g(30, 0)
      g(11, pts[1][0])
      g(21, pts[1][1])
      g(31, 0)
      return
    }
    g(0, 'POLYLINE')
    g(8, layerNameStr)
    g(66, 1)
    g(70, closed ? 1 : 0)
    g(10, 0)
    g(20, 0)
    g(30, 0)
    for (const p of pts) {
      g(0, 'VERTEX')
      g(8, layerNameStr)
      g(10, p[0])
      g(20, p[1])
      g(30, 0)
    }
    g(0, 'SEQEND')
    g(8, layerNameStr)
  }
  const emit = (pl: Polyline2D, layerNameStr: string): void => {
    const pts = pl.points
    if (pts.length < 2) return
    if (pl.circle) {
      emitCircle(pl.circle, layerNameStr)
      return
    }
    if (pl.arcs && pl.arcs.length > 0) {
      // Arcos e rectas alternam: as rectas saem em POLYLINE/LINE, os
      // arcos em ARC. O R12 tem as duas entidades nativamente, por isso
      // não há aproximações nem bulges a inventar.
      const walk = walkPath(pl)
      if (!walk) return
      let buf: Point2[] = [walk.from]
      // Os troços rectos saem sempre ABERTOS: o fecho do contorno é o
      // ponto de volta, acrescentado ao último troço mais abaixo.
      const flush = (): void => {
        if (buf.length >= 2) emitStraight(buf, false, layerNameStr)
        buf = []
      }
      for (const step of walk.steps) {
        if (step.kind === 'arc') {
          flush()
          emitArc(step.arc, layerNameStr)
          buf = [step.to]
        } else {
          buf.push(step.to)
        }
      }
      // Fecho do contorno: o `walkPath` não emite o segmento de volta ao
      // primeiro ponto (é do escritor), mas aqui é preciso à letra.
      if (pl.closed && walk.endIndex !== pts.length) buf.push(pts[0])
      flush()
      return
    }
    emitStraight(pts, pl.closed, layerNameStr)
  }
  for (const entry of layerPlan) {
    for (const pl of entry.layer.projection) emit(pl, entry.projName)
    for (const pl of entry.layer.cut) emit(pl, entry.cutName)
  }
  // TEXT: existe desde o R12, uma linha, sem estilo declarado (o CAD usa
  // o STANDARD). A cota vai em UNIDADES DO DESENHO (metros), convertida
  // dos mm de papel pela escala: 2,5 mm a 1:100 = 0,25 m.
  if (annotLayer) {
    for (const a of notes) {
      g(0, 'TEXT')
      g(8, annotLayer)
      g(10, a.x)
      g(20, a.y)
      g(30, 0)
      g(40, annotationHeightM(a.sizeMm, d.scaleDenom))
      g(1, dxfText(a.text))
    }
  }
  // Cotas: LINE + TEXT na camada COTAS. NÃO se usa a entidade DIMENSION:
  // em R12 ela vive de um bloco anónimo com a geometria já desenhada lá
  // dentro mais uma dúzia de campos de estilo, e cada CAD regenera esse
  // bloco com o SEU estilo de cota ao abrir o ficheiro (setas, unidades
  // e casas decimais que não são as nossas). Linhas e texto lêem-se
  // iguais em todo o lado, e é o que o resto deste writer já faz.
  if (dimLayer) {
    const line = (a: Point2, b: Point2, ltype?: string): void => {
      g(0, 'LINE')
      g(8, dimLayer)
      if (ltype) g(6, ltype)
      g(10, a[0])
      g(20, a[1])
      g(30, 0)
      g(11, b[0])
      g(21, b[1])
      g(31, 0)
    }
    const tickM = dimMmToDrawing(DIM_TICK_MM, d.scaleDenom)
    for (const dim of dims) {
      const geo = dimensionGeometry(dim)
      for (const ln of geo.lines) line(ln.a, ln.b)
      for (const ln of geo.dashed) line(ln.a, ln.b, 'DASHED')
      for (const t of geo.ticks) {
        line(
          [t.at[0] - t.nx * tickM, t.at[1] - t.ny * tickM],
          [t.at[0] + t.nx * tickM, t.at[1] + t.ny * tickM],
        )
      }
      const height = dimensionHeightM(dim.sizeMm, d.scaleDenom)
      for (const t of geo.texts) {
        const offM = dimMmToDrawing(t.offsetMm, d.scaleDenom)
        const x = t.at[0] + t.nx * offM
        const y = t.at[1] + t.ny * offM
        // 72 = 1 (centrado): o ponto que conta passa a ser o 11/21, mas
        // o 10/20 tem de estar lá na mesma (é o que os leitores antigos
        // olham). 50 = rotação em GRAUS, sentido directo.
        g(0, 'TEXT')
        g(8, dimLayer)
        g(10, x)
        g(20, y)
        g(30, 0)
        g(40, height)
        g(1, dxfText(t.text))
        g(50, dxfAngleDeg(t.angleRad))
        g(72, 1)
        g(11, x)
        g(21, y)
        g(31, 0)
      }
    }
  }
  g(0, 'ENDSEC')
  g(0, 'EOF')

  // LF: o DXF ASCII aceita LF puro em todos os CAD correntes e evita a
  // dependência do sistema de ficheiros do utilizador.
  return out.join('\n') + '\n'
}

/** Windows-1252 nos códigos 0x80 a 0x9F (no resto coincide com o
 *  Latin-1). */
const CP1252_EXTRA: Record<number, number> = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85,
  0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a,
  0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92,
  0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97,
  0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c,
  0x017e: 0x9e, 0x0178: 0x9f,
}

/** Bytes do ficheiro DXF na code page declarada (`DXF_CODEPAGE`). Os
 *  caracteres que não existem em Windows-1252 passam a '?'. É assim que
 *  o ficheiro deve ser gravado: um `Blob` de uma string sairia em UTF-8. */
export function dxfToBytes(text: string): Uint8Array<ArrayBuffer> {
  const out: number[] = []
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 63
    if (code < 0x80 || (code >= 0xa0 && code <= 0xff)) out.push(code)
    else out.push(CP1252_EXTRA[code] ?? 63)
  }
  const bytes = new Uint8Array(new ArrayBuffer(out.length))
  bytes.set(out)
  return bytes
}

/** Valor de texto seguro para o DXF ASCII: uma linha (um fim de linha
 *  partiria o par código/valor e corrompia o ficheiro) e sem os códigos
 *  de formatação do MTEXT, que no TEXT sairiam à letra. */
export function dxfText(s: string): string {
  return String(s ?? '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/%%/g, '%')
    .slice(0, 255)
}

/** Ângulo em graus no intervalo [0, 360), como o DXF os quer. */
export function dxfAngleDeg(rad: number): number {
  if (!Number.isFinite(rad)) return 0
  const v = ((rad * 180) / Math.PI) % 360
  return v < 0 ? v + 360 : v
}

/** Nome de camada seguro para R12: maiúsculas, só A-Z 0-9 _ $ - e no
 *  máximo 31 caracteres. */
export function dxfLayerName(name: string): string {
  const s = String(name ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9_$-]/g, '_')
  const t = s.length > 31 ? s.slice(0, 31) : s
  return t.length > 0 ? t : 'GERAL'
}

function num(v: number, decimals: number): string {
  if (!Number.isFinite(v)) return (0).toFixed(decimals)
  const s = v.toFixed(decimals)
  return s === '-' + (0).toFixed(decimals) ? (0).toFixed(decimals) : s
}

// --- Paleta ACI ------------------------------------------------------------
//
// A paleta do AutoCAD tem 255 índices. Os 1-9 e os 250-255 são valores
// tabelados (bem conhecidos); os 10-249 são 24 matizes x 10 variantes
// que aqui se APROXIMAM por HSV (matiz de 15 em 15 graus, saturação
// cheia/meia e cinco níveis de valor). É uma aproximação assumida: o
// objectivo é dar ao CAD um índice visualmente próximo da cor da
// categoria, não reproduzir a tabela ao bit.

const ACI_FIXED: Record<number, string> = {
  1: '#ff0000',
  2: '#ffff00',
  3: '#00ff00',
  4: '#00ffff',
  5: '#0000ff',
  6: '#ff00ff',
  7: '#ffffff',
  8: '#808080',
  9: '#c0c0c0',
  250: '#333333',
  251: '#505050',
  252: '#696969',
  253: '#828282',
  254: '#bebebe',
  255: '#ffffff',
}

const ACI_VALUES = [1, 1, 0.75, 0.75, 0.5, 0.5, 0.35, 0.35, 0.25, 0.25]

let aciCache: Array<[number, number, number]> | null = null

/** RGB 0..255 do índice ACI (1..255). */
export function aciToRgb(index: number): [number, number, number] {
  const table = aciTable()
  const i = Math.min(Math.max(Math.round(index), 1), 255)
  return table[i]
}

function aciTable(): Array<[number, number, number]> {
  if (aciCache) return aciCache
  const t: Array<[number, number, number]> = new Array(256)
  t[0] = [0, 0, 0]
  for (let i = 1; i <= 255; i++) {
    const fixed = ACI_FIXED[i]
    if (fixed) {
      t[i] = hexToRgb(fixed)
      continue
    }
    if (i >= 10 && i <= 249) {
      const group = Math.floor((i - 10) / 10)
      const j = (i - 10) % 10
      const hue = (group * 15) % 360
      const sat = j % 2 === 0 ? 1 : 0.5
      const val = ACI_VALUES[j]
      t[i] = hsvToRgb(hue, sat, val)
      continue
    }
    t[i] = [0, 0, 0]
  }
  aciCache = t
  return t
}

/** Índice ACI mais próximo de um hex CSS. Empates ficam com o índice
 *  mais baixo (as cores primárias 1-6 ganham aos seus clones da grelha
 *  10-249). Preto e branco vão para 7, que no CAD é a cor que segue o
 *  fundo do ecrã e é o que um desenho a preto e branco quer. */
export function hexToAci(hex: string): number {
  const c = normalizeHex(hex)
  if (!c) return 7
  const [r, gg, b] = hexToRgb(c)
  const lum = 0.2126 * r + 0.7152 * gg + 0.0722 * b
  const sat = Math.max(r, gg, b) - Math.min(r, gg, b)
  if (sat < 12 && (lum < 40 || lum > 215)) return 7
  const table = aciTable()
  let best = 7
  let bestD = Infinity
  for (let i = 1; i <= 255; i++) {
    const [tr, tg, tb] = table[i]
    const d = (tr - r) ** 2 + (tg - gg) ** 2 + (tb - b) ** 2
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16)
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
}

function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c
  const sector: [number, number, number] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x]
  const [r, g, b] = sector
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ]
}
